import React from 'react';
import { Shield, Lock, Trash2 } from 'lucide-react';
import { BOOK_PHOTO_RETENTION_SENTENCE, FREE_COVER_PHOTO_RETENTION_SENTENCE, PHOTO_EARLY_DELETION_SENTENCE } from '@/lib/seo/site-facts';

interface PrivacyBadgeProps {
  variant?: 'compact' | 'detailed';
  className?: string;
}

export function PrivacyBadge({ variant = 'compact', className = '' }: PrivacyBadgeProps) {
  if (variant === 'compact') {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2 ${className}`}>
        <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="text-green-800 font-medium">Your photos are secure</p>
          <p className="text-green-700">Used only to make your book, kept private, deleted on request</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="bg-green-100 rounded-full p-2">
          <Shield className="w-5 h-5 text-green-600" />
        </div>
        <h3 className="font-semibold text-green-900">Privacy &amp; Security</h3>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Private Storage:</strong> Your photos are stored in private, access-controlled storage
          </p>
        </div>
        
        <div className="flex items-start gap-2">
          <Trash2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Deletion:</strong> {BOOK_PHOTO_RETENTION_SENTENCE} {FREE_COVER_PHOTO_RETENTION_SENTENCE} {PHOTO_EARLY_DELETION_SENTENCE}
          </p>
        </div>
        
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Never Sold:</strong> We never sell your photos or use them for anything except creating your book. They are sent only to our AI illustration providers to draw the pictures.
          </p>
        </div>
      </div>
      
      <p className="text-xs text-green-700 mt-3 italic">
        We understand the importance of your children's privacy and take it very seriously.
      </p>
    </div>
  );
}
